import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'crm',
  slug: 'crm',
  name: 'CRM',
  description: 'Leads, companies, contacts, and deals for the sales pipeline.',
  icon: 'Handshake',
  endpoints: [
    {
      slug: 'list-leads',
      method: 'GET',
      path: '/api/crmLeads',
      summary: 'List leads',
      description: 'Returns every lead in the pipeline, newest first.',
      auth: 'session',
      responseExample: `[{ "id": "lead_71b", "name": "Alex Rivera", "org": "Example MSP", "source": "referral", "status": "new", "score": 40 }]`,
      sourceFile: 'routes/crm.js, lib/crm.js',
    },
    {
      slug: 'create-lead',
      method: 'POST',
      path: '/api/crmLeads',
      summary: 'Create a lead',
      description: 'Adds a lead to the pipeline. source is normalized to one of: referral, website, event, cold, campaign, email, other.',
      auth: 'session',
      requestBodyExample: `{ "name": "Alex Rivera", "org": "Example MSP", "email": "alex@examplemsp.com", "source": "referral" }`,
      responseExample: `{ "id": "lead_71c", "name": "Alex Rivera", "status": "new", "score": 0 }`,
      sourceFile: 'routes/crm.js, lib/crm.js',
    },
    {
      slug: 'qualify-lead',
      method: 'POST',
      path: '/api/crm/leads/:id/qualify',
      summary: 'Qualify a lead into a deal',
      description: 'Creates or matches the company/contact for this lead and, optionally, opens a deal at the first pipeline stage — all in one call.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Lead id.' }],
      requestBodyExample: `{ "openDeal": true, "dealValue": 1500 }`,
      responseExample: `{ "ok": true, "companyId": "co_5a1", "contactId": "ct_9d2", "dealId": "deal_3e0" }`,
      sourceFile: 'routes/crm.js',
    },
    {
      slug: 'forecast',
      method: 'GET',
      path: '/api/crm/reports/forecast',
      summary: 'Pipeline forecast',
      description: 'Returns likely (weighted value), best-case (full open value), and committed (>=80% probability deals) totals for a close-date window.',
      auth: 'session',
      params: [
        { name: 'from', in: 'query', type: 'string', description: 'Start date, YYYY-MM-DD.' },
        { name: 'to', in: 'query', type: 'string', description: 'End date, YYYY-MM-DD.' },
      ],
      responseExample: `{ "likely": 18400, "bestCase": 41200, "committed": 9000 }`,
      sourceFile: 'routes/crm.js',
    },
    {
      slug: 'list-companies',
      method: 'GET',
      path: '/api/crmCompanies',
      summary: 'List companies',
      description: 'Returns every company (prospective or existing customer) in the CRM, newest first. Companies are the org-level record a lead is qualified into and a deal is attached to.',
      auth: 'session',
      responseExample: `[{ "id": "co_5a1", "name": "Example MSP", "kind": "school", "city": "Springfield", "state": "IL", "status": "prospect", "owner": "" }]`,
      sourceFile: 'routes/crm.js, lib/crm.js',
    },
    {
      slug: 'create-contact',
      method: 'POST',
      path: '/api/crmContacts',
      summary: 'Create a contact',
      description: 'Adds a contact tied to a company. role is normalized to one of: decision_maker, influencer, champion, user, other. Contacts are the people a deal email or activity is logged against.',
      auth: 'session',
      requestBodyExample: `{ "companyId": "co_5a1", "name": "Jordan Lee", "title": "IT Director", "email": "jordan@examplemsp.com", "role": "decision_maker" }`,
      responseExample: `{ "id": "ct_9d2", "companyId": "co_5a1", "name": "Jordan Lee", "role": "decision_maker", "optOut": false }`,
      sourceFile: 'routes/crm.js, lib/crm.js',
    },
    {
      slug: 'convert-deal',
      method: 'POST',
      path: '/api/crm/deals/:id/convert',
      summary: 'Convert a won deal to a customer',
      description: 'Marks the deal\'s company as a customer, creates an onboarding project with a standard checklist (provision tenant, configure SSO, import devices, onboard staff, kickoff call), and optionally opens a contract from the deal\'s value and cadence. Tenant provisioning itself stays a deliberate separate step, not automated here.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Deal id.' }],
      requestBodyExample: `{ "createContract": true }`,
      responseExample: `{ "ok": true, "projectId": "proj_2f0", "contractId": "ctr_8b1", "tenantChecklist": ["Provision tenant", "Configure SSO", "Import devices", "Onboard staff", "Kickoff call"] }`,
      sourceFile: 'routes/crm.js',
    },
    {
      slug: 'reports-summary',
      method: 'GET',
      path: '/api/crm/reports/summary',
      summary: 'Pipeline & campaign summary',
      description: 'Returns deal counts by outcome, an open-pipeline breakdown by stage, win rate, average days to close, overdue-task count, activity mix, win value by lead source, and per-campaign ROI (won value vs. budget, with joined lead counts).',
      auth: 'session',
      responseExample: `{ "counts": { "deals": 52, "open": 19, "won": 27, "lost": 6 }, "openValue": 84200, "winRate": 82, "avgDaysToClose": 34, "overdueTasks": 2, "campaignRoi": [{ "id": "camp_1a", "name": "Fall Open House", "budget": 500, "wonValue": 6200, "wonDeals": 2, "roi": 12.4, "leads": 9 }] }`,
      sourceFile: 'routes/crm.js, lib/crm.js',
    },
    {
      slug: 'settings-get',
      method: 'GET',
      path: '/api/crm/settings',
      summary: 'Get CRM pipeline settings',
      description: 'Returns the tenant\'s configured pipeline stages (id, name, order, and win probability per stage), the lost-reason list shown when a deal is marked lost, and which optional CRM features (sequences, campaigns) are turned on. PUT the same path to change them.',
      auth: 'session',
      responseExample: `{
  "stages": [
    { "id": "qualifying", "name": "Qualifying", "order": 1, "probability": 10 },
    { "id": "proposal", "name": "Proposal", "order": 2, "probability": 50 },
    { "id": "negotiation", "name": "Negotiation", "order": 3, "probability": 75 }
  ],
  "lostReasons": ["Budget", "Chose competitor", "No decision", "Timing"],
  "features": { "sequences": false, "campaigns": true }
}`,
      sourceFile: 'routes/crm.js, lib/crm.js',
    },
    {
      slug: 'board',
      method: 'GET',
      path: '/api/crm/board',
      summary: 'Get the deal pipeline board',
      description: 'Returns every open deal grouped into its pipeline stage column (Kanban view), with each deal\'s company name already joined in — the same read the board UI polls to render drag-and-drop columns.',
      auth: 'session',
      responseExample: `{
  "columns": [
    {
      "id": "qualifying",
      "name": "Qualifying",
      "deals": [
        { "id": "deal_3e0", "title": "Example MSP — Managed Services", "companyName": "Example MSP", "value": 18000, "owner": "jchen" }
      ]
    }
  ]
}`,
      sourceFile: 'routes/crm.js',
    },
    {
      slug: 'deal-email',
      method: 'POST',
      path: '/api/crm/deals/:id/email',
      summary: 'Send an email logged against a deal',
      description: 'Sends an email to a deal\'s primary (or specified) contact and logs it as a deal activity in the same call. Subject/body can come from a saved email template (merge fields resolved against the contact/company/deal) or be supplied inline. Refuses with 409 if the contact has opted out of outreach, and 400 if the contact has no email on file.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Deal id.' }],
      requestBodyExample: `{ "contactId": "ct_9d2", "templateId": "tpl_intro_email" }`,
      responseExample: `{ "ok": true, "sent": true, "simulated": false, "activityId": "act_6f21" }`,
      sourceFile: 'routes/crm.js',
    },
    {
      slug: 'sequence-enroll',
      method: 'POST',
      path: '/api/crm/sequences/:id/enroll',
      summary: 'Enroll a contact into an outreach sequence',
      description: 'Enrolls a contact into a multi-step outreach sequence, gated behind the tenant\'s features.sequences flag (PUT /api/crm/settings) — returns 409 if sequences aren\'t turned on. Nexus enforces contact opt-out on its other outbound CRM sends (POST /api/crm/deals/:id/email refuses to send to an opted-out contact); on the current build, sequence enrollment itself is accepted but the step-by-step send scheduler has not shipped yet, so a successful call acknowledges the enrollment without advancing any sends — check enrolled/note in the response rather than assuming delivery has started.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Sequence id.' }],
      requestBodyExample: `{ "contactId": "ct_9d2" }`,
      responseExample: `{ "ok": true, "enrolled": false, "note": "sequences not yet implemented (phase 2)" }`,
      sourceFile: 'routes/crm.js',
    },
  ],
};
